import { AnchorType } from 'types'
import Styles from './LoadingScreen.module.scss'
import { useProgress } from 'hooks/useProgress'
import { Loading } from './Loading'
import { useButton } from '@react-aria/button'
import { useObjectRef } from 'utils/useObjectRef'
import { INTERCOM_AUTOPILOT_ONBOARDING_MESSAGE } from 'components/FirstTimeExperienceModal/components/SupportChat/SupportChat'
import { Pressable } from '@react-aria/interactions'
import { Text } from 'blaze-ui/Text/Text'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { useEffect } from 'react'
import cx from 'classnames'

interface LoadingScreenProps {
  anchorType: AnchorType
  screenshotUrl?: string
  previewUrl?: string
  realProgress?: number
  labels: string[]
  header?: string
}

export const LoadingScreen = ({
  anchorType,
  screenshotUrl,
  realProgress,
  labels,
  previewUrl,
  header = 'Reviewing your anchor content 👀',
}: LoadingScreenProps) => {
  const usingRealProgress = realProgress !== undefined
  const buttonRef = useObjectRef<HTMLButtonElement>(null)
  const { buttonProps } = useButton(
    { onPress: () => window.Intercom('showNewMessage', INTERCOM_AUTOPILOT_ONBOARDING_MESSAGE) },
    buttonRef,
  )

  const {
    start: startThree,
    isRunning: isRunningThree,
    progress: progressThree,
  } = useProgress({ linearProgress: true, speed: 100 })
  const {
    start: startTwo,
    isRunning: isRunningTwo,
    progress: progressTwo,
  } = useProgress({ linearProgress: true, speed: 100, immediateComplete: true, onDone: startThree })
  const {
    start: startOne,
    isRunning: isRunningOne,
    progress: progressOne,
  } = useProgress({ linearProgress: true, speed: 100, immediateComplete: true, onDone: startTwo })

  if (!isRunningOne && progressOne !== 100 && !usingRealProgress) {
    startOne()
  }

  useEffect(() => {
    if (realProgress && realProgress === 100) {
      startTwo()
    }
  }, [realProgress, startTwo])

  return (
    <div className={Styles.loadingScreen}>
      <Loading
        header={header}
        progresses={[
          {
            title: labels[0],
            isRunning: !!(usingRealProgress && realProgress < 100) || isRunningOne,
            percentComplete: usingRealProgress ? realProgress : progressOne,
          },
          {
            title: labels[1],
            isRunning: isRunningTwo,
            percentComplete: progressTwo,
          },
          {
            title: labels[2],
            isRunning: isRunningThree,
            percentComplete: progressThree,
          },
        ]}
      >
        {anchorType === 'website' ? (
          <div className={cx(Styles.imageContainer, { [Styles.withScreenshot]: !!screenshotUrl })}>
            {screenshotUrl ? (
              <img src={screenshotUrl} alt="Initial Screenshot" />
            ) : (
              <LoadingShimmerer>
                <div className={Styles.screenshotPlaceholder} />
              </LoadingShimmerer>
            )}
          </div>
        ) : (
          <img
            src={previewUrl}
            alt="Loading"
            className={cx(Styles.loadingImage, { [Styles.video]: anchorType === 'video' })}
          />
        )}
      </Loading>

      <div className={Styles.helpButton}>
        <Pressable onPress={() => window.Intercom('showNewMessage', INTERCOM_AUTOPILOT_ONBOARDING_MESSAGE)}>
          <button className={Styles.supportButton} ref={buttonRef} {...buttonProps}>
            <div className={Styles.avatar}>
              <img
                className={Styles.face}
                src="https://res.cloudinary.com/almanac/image/upload/v1756854485/blaze_assets/virtual_marketer/strategies/face_jsgnb8.png"
                alt="Support Avatar"
              />
            </div>
            <Text variant="smallList">Get Live Help</Text>
          </button>
        </Pressable>
      </div>
    </div>
  )
}
